import { Text } from '@chakra-ui/react'
import isHotkey from 'is-hotkey'
import { debounce, omit } from 'lodash-es'
import React, {
  type CSSProperties,
  forwardRef,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { createEditor, type Descendant, Editor, Range, Transforms } from 'slate'
import { withHistory } from 'slate-history'
import {
  Editable,
  ReactEditor,
  type RenderElementProps,
  type RenderLeafProps,
  Slate,
  withReact,
} from 'slate-react'

import { noOp } from '@/utils/helpers'

import { EMPTY_RICH_TEXT_VALUE } from '@/client/services/richText/constants'
import deserializeRichText from '@/client/services/richText/deserializeRichText'
import plainTextToSerializedRichText from '@/client/services/richText/plainTextToSerializedRichText'
import serializeRichText from '@/client/services/richText/serializeRichText'
import type { SerializedRichText } from '@/client/services/richText/types'

import type { RichTextEditorRef } from '../types'
import { resetEditor } from './helpers'
import { withImages } from './image'
import { insertMention, withInlines } from './inlines'
import { useMentionsContext } from './Mention/context'
import { MENTIONS_QUERY_THRESHOLD } from './Mention/MentionsContextProvider'
import { toggleMark } from './mark'
import { withMath } from './math'
import RichTextElement from './RichTextElement'
import RichTextLeaf from './RichTextLeaf'
import type { RichTextEditorProps, SlateMark } from './types'

const HOTKEYS: Record<Exclude<SlateMark, 'color'>, string> = {
  bold: 'mod+b',
  italic: 'mod+i',
  underline: 'mod+u',
}

const MENTION_DEBOUNCE_DELAY_MS = 300

const RichTextEditorBase = forwardRef<
  RichTextEditorRef,
  RichTextEditorProps & {
    header?: () => ReactElement | null
    footer?: () => ReactElement | null
    style: CSSProperties
  }
>(
  (
    {
      name,
      placeholder,
      initialValue = EMPTY_RICH_TEXT_VALUE,
      disabled = false,
      onEnter,
      onChange = noOp,
      header,
      footer,
      style,
      height,
      maxHeight,
      disableNewLineOnEnter = false,
      onFilesUpload = noOp,
      disableAutofocus,
    },
    ref
  ): ReactElement => {
    const mentions = useMentionsContext()

    const [value, setValue] = useState<SerializedRichText>(
      typeof initialValue === 'string' ? plainTextToSerializedRichText(initialValue) : initialValue
    )

    const renderElement = useCallback(
      (props: RenderElementProps) => <RichTextElement {...props} />,
      []
    )
    const renderLeaf = useCallback((props: RenderLeafProps) => <RichTextLeaf {...props} />, [])
    const editor = useMemo(() => {
      const modifiedEditor = withMath(withInlines(withHistory(withReact(createEditor()))))
      return withImages(modifiedEditor, { onFilesUpload })
      // If onFilesUpload is re-rendered, then the editor re-renders, which causes an error.
      // TODO: Find a better solution.
      // oxlint-ignore-next-line react/exhaustive-deps
    }, [])

    React.useImperativeHandle(
      ref,
      () => ({
        clear: () => {
          resetEditor(editor)
          setValue(EMPTY_RICH_TEXT_VALUE)
        },
        setValue: val => {
          editor.children = deserializeRichText(val)
          Transforms.select(editor, Editor.end(editor, []))
        },
        focus: () => {
          ReactEditor.focus(editor)
        },
      }),
      [editor]
    )

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (mentions.rangeToReplace == null) {
          if (event.key === 'Enter' && disableNewLineOnEnter) {
            event.preventDefault()
            if (event.shiftKey || event.ctrlKey || event.altKey) {
              Transforms.insertText(editor, '\n')
              return
            }
            onEnter?.()
            return
          }
        } // if mentions menu is open, handle arrow key navigation
        else if (event.key === 'ArrowUp') {
          event.preventDefault()
          const nextIndex =
            mentions.menuIndex <= 0 ? mentions.userList.length - 1 : mentions.menuIndex - 1
          mentions.setMenuIndex(nextIndex)
        } else if (event.key === 'ArrowDown') {
          event.preventDefault()
          const prevIndex =
            mentions.menuIndex >= mentions.userList.length - 1 ? 0 : mentions.menuIndex + 1
          mentions.setMenuIndex(prevIndex)
        }
        // if enter or tab is pressed, insert mention for selected user
        else if ((event.key === 'Enter' || event.key === 'Tab') && mentions.menuIndex >= 0) {
          event.preventDefault()
          Transforms.select(editor, mentions.rangeToReplace)
          const user = mentions.userList[mentions.menuIndex]
          insertMention(editor, user)
          mentions.closeMentionsMenu()
        } else if (event.key === 'Escape' || event.key === ' ') {
          mentions.closeMentionsMenu()
        }

        const { selection } = editor
        Object.entries(HOTKEYS).forEach(([format, hotkey]) => {
          if (isHotkey(hotkey, event)) {
            event.preventDefault()
            toggleMark(editor, format as SlateMark)
          }
        })
        if (selection != null && Range.isCollapsed(selection)) {
          if (isHotkey('left', event)) {
            event.preventDefault()
            Transforms.move(editor, { unit: 'offset', reverse: true })
          }
          if (isHotkey('right', event)) {
            event.preventDefault()
            Transforms.move(editor, { unit: 'offset' })
          }
        }
      },
      [disableNewLineOnEnter, editor, mentions, onEnter]
    )

    const handleMention = useCallback(async () => {
      // range selection logic from https://www.slatejs.org/examples/mentions
      const { selection } = editor

      if (selection && Range.isCollapsed(selection)) {
        // check if word unit before cursor starts with @ symbol followed by filter
        const [start] = Range.edges(selection)
        const wordBefore = Editor.before(editor, start, { unit: 'word' })
        const before = wordBefore && Editor.before(editor, wordBefore)
        const beforeRange = before && Editor.range(editor, before, start)
        const beforeText = beforeRange && Editor.string(editor, beforeRange)
        const beforeMatch = beforeText != null ? /^@(\w*)$/.exec(beforeText) : null
        // check if whitespace is after cursor
        const after = Editor.after(editor, start)
        const afterRange = Editor.range(editor, start, after)
        const afterText = Editor.string(editor, afterRange)
        const afterMatch = /^(\s|$)/.exec(afterText)

        if (beforeMatch && afterMatch && beforeRange) {
          const filter = beforeMatch[1]
          mentions.setRangeToReplace(beforeRange)
          mentions.setFilter(filter)

          // query backend for mentionable users if filter length is equal to query threshold
          if (mentions.getMentionSuggestions != null) {
            if (beforeMatch[1].length >= MENTIONS_QUERY_THRESHOLD) {
              const users = await mentions.getMentionSuggestions(filter)
              mentions.setUserList(users)
            } else if (beforeMatch[1].length < MENTIONS_QUERY_THRESHOLD) {
              mentions.setUserList(
                mentions.usersInThread.filter(user =>
                  user.displayName.toLowerCase().startsWith(filter.toLowerCase())
                )
              )
            }
          }
          return
        }
      }
      mentions.closeMentionsMenu()
    }, [editor, mentions])

    const handleMentionDebounced = useMemo(
      () => debounce(handleMention, MENTION_DEBOUNCE_DELAY_MS),
      [handleMention]
    )

    useEffect(() => () => handleMentionDebounced.cancel(), [handleMentionDebounced])

    const handleChange = useCallback(
      async (val: Descendant[]) => {
        const isAstChange = editor.operations.some(op => op.type !== 'set_selection')
        if (!isAstChange) {
          return
        }
        const newValue = serializeRichText(val)

        setValue(newValue)
        onChange(newValue)

        await handleMentionDebounced()
      },
      [editor.operations, handleMentionDebounced, onChange]
    )

    return (
      <Slate editor={editor} value={deserializeRichText(value)} onChange={handleChange}>
        {header?.()}
        <Editable
          name={name}
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          placeholder={placeholder}
          renderPlaceholder={({ children, attributes }) => (
            <Text {...omit(attributes, 'style.opacity')} color="fg.subtle">
              {children}
            </Text>
          )}
          spellCheck
          autoFocus={disableAutofocus !== true}
          onKeyDown={handleKeyDown}
          readOnly={disabled}
          style={{ ...style, height, maxHeight }}
        />
        {footer?.()}
      </Slate>
    )
  }
)

export default RichTextEditorBase
